import { FormEvent, useEffect, useState } from 'react';
import { useGetAuftraegeMutation } from '../../api/AuftragAPI';
import { IAuftrag } from '../../types/Types';
import { formatToEuroDate } from '../../util/DateUtils';
import ButtonStartNaviComponent from '../ButtonStartNaviComponent/ButtonStartNaviComponent';
import styles from './calendarcomponent.module.scss';
import { useWebSocket } from '../../util/Websocket';
import ButtonCopyComponent from '../ButtonCopyComponent/ButtonCopyComponent';

/**
 * @author noone
 */
const CalendarComponent: React.FC = () => {

  const [getAuftraegeAPI] = useGetAuftraegeMutation();

  const [auftraege, setAuftraege] = useState<IAuftrag[]>([]);
  const [getVon, setVon] = useState<Date>(new Date());
  const [bis, setBis] = useState<Date>(new Date());
  const [getTagZumAnzeigen, setTagZumAnzeigen] = useState<Date>(new Date());
  const [tage, setTage] = useState<Map<number, Date>>(new Map());
  const [expandedAppointments, setExpandedAppointments] = useState<Set<string>>(new Set());

  const { messages, send } = useWebSocket("termin");

  const lade = () => {
    getAuftraegeAPI({ status: ["TERMINIERT" , "FERTIG", "FAKTURIERBAR"], terminVon: getVon, terminBis: bis }).unwrap()
      .then(data => setAuftraege(data));
  }

  const eineWocheZurück = () => {
    setVon(new Date(getVon.setDate(getVon.getDate() - 7)));
  }

  const eineWocheSpäter = () => {
    setVon(new Date(getVon.setDate(getVon.getDate() + 7)));
  }

  function isSameDay(date1: Date | null, date2: Date): boolean {
    return formatToEuroDate(date1) === formatToEuroDate(date2);
  }

  const toggleExpandAppointment = (appointmentId: string) => {
    const newExpanded = new Set(expandedAppointments);
    if (newExpanded.has(appointmentId)) {
      newExpanded.delete(appointmentId);
    } else {
      newExpanded.add(appointmentId);
    }
    setExpandedAppointments(newExpanded);
  }

  // Stunden 7..22
  const hours = Array.from({ length: 16 }, (_, i) => i + 7);
  const HOUR_HEIGHT = 80; // px per hour row, used to compute quarter-hour positions

  const getAppointmentsForDayAndHour = (day: Date, hour: number) => {
    return auftraege.filter(a => {
      if (!a.aktuellerTermin) return false;
      const d = new Date(a.aktuellerTermin);
      return isSameDay(d, day) && d.getHours() === hour;
    });
  }

  // Berechne die Höhe basierend auf der Dauer in Minuten
  const getAppointmentHeight = (durationMinutes: number): number => {
    return (durationMinutes / 60) * HOUR_HEIGHT;
  }

  // Gruppiere Appointments mit gleicher Start-Zeit und berechne ihre Position/Breite
  const getAppointmentPositioning = (apps: IAuftrag[], appointmentIndex: number) => {
    if (apps.length <= 1) {
      return { left: 6, width: 'calc(100% - 12px)', position: 0, count: 1 };
    }

    // Berechne für jedes Appointment Start- und End-Minute (relativ zur Stunde)
    type Item = { idx: number; start: number; end: number };
    const items: Item[] = apps.map((a, i) => {
      const d = new Date(a.aktuellerTermin as Date);
      const start = d.getMinutes();
      const end = start + (a.dauerInMinuten || 0);
      return { idx: i, start, end };
    });

    // Sortiere nach Startzeit (frühere zuerst)
    const sorted = items.slice().sort((x, y) => x.start - y.start || y.end - x.end);

    // Greedy-Zuweisung auf Spalten: jede Spalte merkt sich das Ende des letzten Events
    const columnsEnd: number[] = [];
    const assigned: number[] = new Array(items.length).fill(0);

    for (const it of sorted) {
      let placed = false;
      for (let c = 0; c < columnsEnd.length; c++) {
        if (it.start >= columnsEnd[c]) {
          assigned[it.idx] = c;
          columnsEnd[c] = it.end;
          placed = true;
          break;
        }
      }
      if (!placed) {
        const newCol = columnsEnd.length;
        columnsEnd.push(it.end);
        assigned[it.idx] = newCol;
      }
    }

    const colIndex = assigned[appointmentIndex];
    const colCount = columnsEnd.length;
    const widthPercent = 100 / colCount;
    const leftPercent = colIndex * widthPercent;

    const margin = 6; // px Marge zwischen Appointments
    const width = `calc(${widthPercent}% - ${margin}px)`;
    const left = `calc(${leftPercent}% + ${colIndex > 0 ? margin : 6}px)`;

    return { left, width, position: colIndex, count: colCount };
  }

  useEffect(() => {
    const newTage = new Map<number, Date>();
    for (let i = 0; i < 5; i++) {
      const tag = new Date(getVon);
      tag.setDate(getVon.getDate() + i); // <-- hier werden die Folgetage erzeugt
      newTage.set(i, tag);
    }
    setTage(newTage);

    const bis = new Date(getVon);
    bis.setDate(bis.getDate() + 6);
    setBis(bis);
    
  }, [getVon]);

  useEffect(() => {
    lade();
  }, [bis]);

  useEffect(() => {
    if (messages.length === 0) return;
    const satz = messages.at(-1);
    if (satz?.type === "TERMINAENDERUNG") {
      lade();
    }
  }, [messages]);

  useEffect(() => {
    const montag = new Date();
    const day = montag.getDay();
    const diff = (day + 6) % 7;
    montag.setDate(montag.getDate() - diff);
    setVon(montag);
  }, []);

  return (
    <>

      <p>
        Anzahl Aufträge im Zeitraum von {formatToEuroDate(getVon)} bis {formatToEuroDate(bis)}: {auftraege.length}
      </p>

      <button className="btn btn-light" onClick={eineWocheZurück}><i className="bi bi-arrow-left"></i>Früher</button>
      <button className="btn btn-light" onClick={eineWocheSpäter}>Später<i className="bi bi-arrow-right"></i></button>

      <div className={styles.aussendiensttage}>
        {Array.from(tage.entries()).map(([index, date]) => (  
          <div key={index} className={styles.dayColumn + " card"}>
            <div className={styles.dayHeader}>
              <button className="btn btn-secondary m-1" onClick={() => setTagZumAnzeigen(date)}>
                {(index === 0) ? "Montag" : index === 1 ? "Dienstag" : index === 2 ? "Mittwoch" : index === 3 ? "Donnerstag" : index === 4 ? "Freitag" : "Unbekannt"}<br />
                {formatToEuroDate(date)}
              </button>
            </div>

            <div className={styles.hoursContainer}>
              {hours.map(h => {
                const apps = getAppointmentsForDayAndHour(date, h);
                return (
                  <div key={h} className={styles.hourRow}>
                    <div className={styles.hourLabel}>{h}:00</div>
                    <div className={styles.hourContent}>
                      {apps.length === 0 && <div className={styles.hourEmpty}></div>}
                      {apps.map((auftrag, idx) => {
                        const d = new Date(auftrag.aktuellerTermin as Date);
                        const minutes = d.getMinutes();
                        // position within the hour in pixels
                        const top = (minutes / 60) * HOUR_HEIGHT;
                        // height basierend auf der Dauer in Minuten
                        const height = getAppointmentHeight(auftrag.dauerInMinuten);
                        // Positionierung für nebeneinander angeordnete Termine
                        const positioning = getAppointmentPositioning(apps, idx);
                        const appointmentId = `${auftrag.uid}-${h}-${idx}`;
                        const isExpanded = expandedAppointments.has(appointmentId);
                        return (
                          <div
                            key={idx}
                            className={`${styles.appointment} ${isExpanded ? styles.appointmentExpanded : ''}`}
                            style={{
                              borderLeft: "4px solid " + auftrag.colorProfile,
                              position: 'absolute',
                              left: isExpanded ? 6 : positioning.left,
                              right: isExpanded ? 6 : 'auto',
                              width: isExpanded ? 'auto' : positioning.width,
                              top: isExpanded ? 0 : top,
                              height: isExpanded ? 'auto' : height,
                              zIndex: isExpanded ? 10 : 1
                            }}
                          >
                            <div className={styles.appointmentHeader}>
                              <div className={styles.appointmentTitle}>
                                {d.getHours().toString().padStart(2, '0')}:{d.getMinutes().toString().padStart(2, '0')} - {auftrag.stadt_lieferadresse} {auftrag.name_endkunde}
                              </div>
                              <button
                                className={`btn btn-sm ${styles.expandButton}`}
                                onClick={(event: FormEvent) => {
                                  event.stopPropagation();
                                  toggleExpandAppointment(appointmentId);
                                }}
                                title={isExpanded ? "Verkleinern" : "Vergrößern"}
                              >
                                <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                              </button>
                            </div>
                            {isExpanded && (
                              <div className={styles.appointmentActions}>
                                <ButtonStartNaviComponent auftrag={auftrag} />

                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={(event: FormEvent) => {
                                    event.stopPropagation();
                                    window.open(
                                      "./.auftragdetails?uid=" + auftrag?.uid + "&backbuttonClose=true",
                                      "_blank"
                                    );
                                  }} ><i className='bi bi-pencil-fill'></i></button>

                                  <ButtonCopyComponent textToCopy={""
                                                  + auftrag?.strasse_lieferadresse + " "
                                                  + auftrag?.hausnummer_lieferadresse + "\n"
                                                  + auftrag?.postleitzahl_lieferadresse + " "
                                                  + auftrag?.stadt_lieferadresse} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

          </div>

        ))}
      </div>

    </>
  );

}

export default CalendarComponent;